// src/components/development/DevAppHeader.tsx
import React from 'react';
import {
  ChevronsUpDown, LayoutGrid, Bot, Boxes, ShoppingCart, Home, Calendar, Eye, Layers, Wrench, Heart, HandHeart, HelpCircle, Handshake, FileText,
  BookOpen // <-- NEW: Import the icon for the blog
} from 'lucide-react';
import clsx from 'clsx';
import {Tooltip} from '@dopomogai/ui';

interface NavButtonProps {
  icon: React.ReactNode;
  tooltipText: string;
  isActive: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, tooltipText, isActive, onClick }) => (
  <Tooltip text={tooltipText} position="bottom">
    <button
      onClick={onClick}
      className={clsx(
        "flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 focus-visible:ring-sky-400",
        isActive
          ? "bg-sky-500/20 text-sky-300"
          : "text-gray-400 hover:text-white hover:bg-white/10"
      )}
      aria-label={tooltipText}
    >
      {icon}
    </button>
  </Tooltip>
);

interface DevAppHeaderProps {
  agentScenarios: ScenarioOption[];
  solutionScenarios: ScenarioOption[];
  currentAgentId: string | undefined;
  currentSolutionId: string | undefined;
  pageType: 'homepage' | 'agent' | 'solution' | 'teams_showcase' | 'team_detail' | 'agents_marketplace' | 'roadmap' | 'vision' | 'platform' | 'solutions' | 'impact' | 'support_us' | 'faq' | 'co_ownership' | 'whitepaper' | 'legal' | 'unknown' | 'blog_index' | 'blog_article'; // <-- NEW: Add blog page types
  viewMode: 'public_showcase' | 'owner_dashboard';
  onAgentChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onSolutionChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onViewModeToggle: () => void;
  onViewTeams: () => void;
  onViewMarketplace: () => void;
  onViewHome: () => void;
  onViewRoadmap: () => void;
  onViewVision: () => void;
  onViewPlatform: () => void;
  onViewSolutions: () => void;
  onViewImpact: () => void;
  onViewSupportUs: () => void;
  onViewFaq: () => void;
  onViewCoOwnership: () => void;
  onViewWhitepaper: () => void;
  onViewBlog: () => void; // <-- NEW: Add handler prop
}

interface ScenarioOption {
  id: string;
  name: string;
}

export const DevAppHeader: React.FC<DevAppHeaderProps> = ({
  agentScenarios,
  solutionScenarios,
  currentAgentId,
  currentSolutionId,
  pageType,
  viewMode,
  onAgentChange,
  onSolutionChange,
  onViewModeToggle,
  onViewTeams,
  onViewMarketplace,
  onViewHome,
  onViewRoadmap,
  onViewVision,
  onViewPlatform,
  onViewSolutions,
  onViewImpact,
  onViewSupportUs,
  onViewFaq,
  onViewCoOwnership,
  onViewWhitepaper,
  onViewBlog, // <-- NEW: Destructure handler
}) => {

  return (
    <header className="sticky top-0 z-50 bg-gray-900 border-b border-white/10 shadow-md app-header-sticky">
      <div className="container mx-auto flex items-center justify-between h-[68px] px-4">
        {/* Left Side: Title and Main Navigation */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-white">
            <LayoutGrid size={20} className="text-sky-400"/>
            <span className="font-bold text-lg">Dopomogai Dev</span>
          </div>
          <div className="h-8 w-px bg-white/10" />
          <nav className="flex items-center gap-1">
             <NavButton tooltipText="Home"         icon={<Home size={20}/>}       isActive={pageType === 'homepage'}            onClick={onViewHome} />
             <NavButton tooltipText="Vision"       icon={<Eye size={20}/>}        isActive={pageType === 'vision'}              onClick={onViewVision} />
             <NavButton tooltipText="Impact"       icon={<Heart size={20}/>}      isActive={pageType === 'impact'}              onClick={onViewImpact} />
             <NavButton tooltipText="Support Us"   icon={<HandHeart size={20}/>}  isActive={pageType === 'support_us'}          onClick={onViewSupportUs} />
             <NavButton tooltipText="FAQ"          icon={<HelpCircle size={20}/>} isActive={pageType === 'faq'}                 onClick={onViewFaq} />
             <NavButton tooltipText="Platform"     icon={<Layers size={20}/>}     isActive={pageType === 'platform'}            onClick={onViewPlatform} />
             <NavButton tooltipText="Co-Ownership" icon={<Handshake size={20}/>}  isActive={pageType === 'co_ownership'}        onClick={onViewCoOwnership} />
             <NavButton tooltipText="Whitepaper"   icon={<FileText size={20}/>}   isActive={pageType === 'whitepaper'}          onClick={onViewWhitepaper} />
             <NavButton tooltipText="Blog / Insights"   icon={<BookOpen size={20}/>}   isActive={pageType === 'blog_index' || pageType === 'blog_article'} onClick={onViewBlog} /> {/* <-- NEW: Blog Button */}
             <NavButton tooltipText="Solutions"    icon={<Wrench size={20}/>}     isActive={pageType === 'solutions'}           onClick={onViewSolutions} />
             <NavButton tooltipText="Roadmap"      icon={<Calendar size={20}/>}   isActive={pageType === 'roadmap'}             onClick={onViewRoadmap} />
             <NavButton tooltipText="AI Teams"     icon={<Boxes size={20}/>}      isActive={pageType === 'teams_showcase' || pageType === 'team_detail'} onClick={onViewTeams} />
             <NavButton tooltipText="Marketplace"  icon={<ShoppingCart size={20}/>}isActive={pageType === 'agents_marketplace'}  onClick={onViewMarketplace} />
          </nav>
        </div>

        {/* Right Side: Contextual Controls and View Toggle */}
        <div className="flex items-center gap-4">
          <div className="relative">
             <Bot size={16} className={clsx("absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors", pageType === 'agent' ? "text-sky-400" : "text-gray-500")}/>
             <select value={currentAgentId || ''} onChange={onAgentChange} className={clsx("pl-9 pr-8 py-2 text-sm rounded-md border appearance-none transition-all bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-sky-400", pageType === 'agent' ? "border-sky-500/50" : "border-white/10 hover:border-white/20")} aria-label="Select Agent">
                <option value="" disabled>-- Select an Agent --</option>
                {agentScenarios && agentScenarios.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
            <ChevronsUpDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"/>
          </div>
           <div className="relative">
             <LayoutGrid size={16} className={clsx("absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors", pageType === 'solution' ? "text-sky-400" : "text-gray-500")}/>
             <select value={currentSolutionId || ''} onChange={onSolutionChange} className={clsx("pl-9 pr-8 py-2 text-sm rounded-md border appearance-none transition-all bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-sky-400", pageType === 'solution' ? "border-sky-500/50" : "border-white/10 hover:border-white/20")} aria-label="Select Solution">
                <option value="" disabled>-- Select a Solution --</option>
                {solutionScenarios && solutionScenarios.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
            <ChevronsUpDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"/>
          </div>
          <div className="h-8 w-px bg-white/10" />
          <div className="flex items-center p-1 rounded-lg bg-gray-800 border border-white/10">
            <button onClick={viewMode !== 'public_showcase' ? onViewModeToggle : undefined} className={clsx("px-3 py-1 text-sm font-medium rounded-md transition-colors", viewMode === 'public_showcase' ? 'bg-sky-500 text-white shadow-sm' : 'text-gray-400 hover:text-white')}>Public</button>
            <button onClick={viewMode !== 'owner_dashboard' ? onViewModeToggle : undefined} className={clsx("px-3 py-1 text-sm font-medium rounded-md transition-colors", viewMode === 'owner_dashboard' ? 'bg-sky-500 text-white shadow-sm' : 'text-gray-400 hover:text-white')}>Owner</button>
          </div>
        </div>
      </div>
    </header>
  );
};

